import React, { type JSX } from 'react';
import { Box, Typography, Stack, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CustomTransComponent } from 'components';
import { LoadGroupsListHierarchy } from './LoadsGroupsDetails';

type SelectedLoad = {
    id: string;
    name?: string;
    loadName?: string;
    loadNumber?: number;
};

type MultiDeviceLoadStepProps = {
    createPayload: any;
    setCreatePayload: React.Dispatch<React.SetStateAction<any>>;
    selectedLoads: SelectedLoad[];
    setSelectedLoads: React.Dispatch<React.SetStateAction<SelectedLoad[]>>;
};

export const MultiDeviceLoadStep = ({
    createPayload,
    setCreatePayload,
    selectedLoads,
    setSelectedLoads,
}: MultiDeviceLoadStepProps): JSX.Element => {
    const syncSelectedLoads = (loads: SelectedLoad[]): void => {
        setSelectedLoads(loads);
        setCreatePayload((prev: any) => ({
            ...prev,
            loadIds: loads.map((item) => item.id),
        }));
    };

    const handleToggleLoad = (_loadId: string, load: SelectedLoad): void => {
        if (!load?.id) {
            return;
        }

        const exists = selectedLoads.some((item) => item.id === load.id);
        const nextSelectedLoads = exists
            ? selectedLoads.filter((item) => item.id !== load.id)
            : [...selectedLoads, load];

        syncSelectedLoads(nextSelectedLoads);
    };

    const handleRemoveLoad = (loadId: string): void => {
        syncSelectedLoads(selectedLoads.filter((item) => item.id !== loadId));
    };

    return (
        <Box p={2} display="flex" flexDirection="column">
            <Box display="flex" justifyContent={'space-between'}>
                <Box p={2} sx={{ width: '49%', backgroundColor: '#f2f2f2' }}>
                    <Typography variant="h6" gutterBottom fontSize={16} fontWeight={600} color="#424e54">
                        <CustomTransComponent translationKey={'WIDGETS:LOADS.SELECT_LOADS_WITHOUT_COUNT'} />
                    </Typography>
                    <Box className="max-height-570 bg-white">
                        <LoadGroupsListHierarchy
                            ignoreDefaultAccess={true}
                            selectedLoadIds={createPayload?.loadIds ?? []}
                            onToggleLoad={handleToggleLoad}
                        />
                    </Box>
                </Box>

                <Box p={2} sx={{ width: '49%', backgroundColor: '#f2f2f2' }}>
                    <Typography fontSize={16} fontWeight={600} color="#424e54" variant="h6" mb={1}>
                        <CustomTransComponent translationKey={'COMMON:SELECTED_LOADS'} />
                    </Typography>
                    <Typography fontSize={16} fontWeight={400} color="#424e54" variant="h6" mb={2}>
                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.SELECT_SIXTEEN_LOADS'} />
                    </Typography>
                    <Typography variant="h6" mb={1} fontSize={12} fontWeight={600} color="#424e54">
                        <CustomTransComponent translationKey={'COMMON:SELECTED_LOADS'} /> ({selectedLoads.length}/16)
                    </Typography>

                    <Stack spacing={1}>
                        {selectedLoads.map((item) => (
                            <Box
                                key={item.id}
                                sx={{
                                    p: 1,
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderBottomWidth: '2px',
                                    borderBottomColor: '#727e84',
                                }}
                            >
                                <Stack flexDirection={'row'} alignItems={'center'}>
                                    <Box
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            backgroundColor: '#727E84',
                                            color: '#fff',
                                            width: '18px',
                                            height: '18px',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {(item?.loadNumber ?? 0) + 1}
                                    </Box>
                                    <Typography fontSize={14} fontWeight={400} color="#424e54" pl={1}>
                                        {item?.loadName ?? item?.name ?? ''}
                                    </Typography>
                                </Stack>
                                <IconButton size="small" onClick={() => handleRemoveLoad(item.id)}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};
